import { useState, useCallback } from 'react';
import {
  CachedPlayer,
  KYPSearchState,
  KYPSearchStateActions
} from '../types/kyp-search.types';

export interface KYPSearchCache {
  recentPlayers: CachedPlayer[];
  addPlayer: (player: CachedPlayer) => void;
  findPlayer: (id: string) => CachedPlayer | undefined;
  findPlayerByName: (name: string) => CachedPlayer | undefined;
  loadCachedPlayer: (player: CachedPlayer) => void;
  clearCache: () => void;
}

export const useKYPSearchCache = (
  state: KYPSearchState,
  actions: KYPSearchStateActions
): KYPSearchCache => {
  const [recentPlayers, setRecentPlayers] = useState<CachedPlayer[]>([]);

  const addPlayer = useCallback((player: CachedPlayer) => {
    setRecentPlayers(prev => {
      const filtered = prev.filter(p => p.id !== player.id);
      return [player, ...filtered].slice(0, 10);
    });
  }, []);

  const findPlayer = useCallback((id: string) => {
    return recentPlayers.find(p => p.id === id);
  }, [recentPlayers]);

  const findPlayerByName = useCallback((name: string) => {
    return recentPlayers.find(p => 
      p.name.toLowerCase() === name.toLowerCase()
    );
  }, [recentPlayers]);

  const loadCachedPlayer = useCallback((player: CachedPlayer) => {
    // Set all player data from cache
    actions.setPlayerName(player.name);
    actions.setPlayerId(player.id);
    
    // Process the player data to ensure proper JSON parsing
    const processedData = { ...player.data };
    
    // For each object property that might be a string, try to parse it
    for (const key in processedData) {
      const value = processedData[key as keyof typeof processedData];
      if (typeof value === 'string') {
        try {
          processedData[key as keyof typeof processedData] = JSON.parse(value);
        } catch (e) {
          // If parsing fails, wrap it in a summary object
          processedData[key as keyof typeof processedData] = { summary: value };
        }
      }
    }
    
    actions.setPlayerData(processedData);
    actions.setPlayerProfile(player.profile);
    actions.setPlayerInjuries(player.injuries || null);
    actions.setPlayerTransfers(player.transfers || null);
    actions.setPlayerAchievements(player.achievements || null);
    actions.setPlayerStats(player.stats || null);
    
    // Calculate and set career stats if player stats are available
    if (player.stats && player.stats.length > 0) {
      const careerStats = player.stats.reduce((acc, stat) => {
        return {
          ...acc,
          appearances: acc.appearances + stat.appearances,
          goals: acc.goals + stat.goals,
          assists: acc.assists + stat.assists,
          yellow_cards: acc.yellow_cards + stat.yellow_cards,
          red_cards: acc.red_cards + stat.red_cards,
          minutes_played: acc.minutes_played + stat.minutes_played
        };
      }, {
        appearances: 0,
        goals: 0,
        assists: 0,
        yellow_cards: 0,
        red_cards: 0,
        minutes_played: 0
      });
      
      actions.setPlayerCareerStats(careerStats);
    } else {
      actions.setPlayerCareerStats(null);
    }
    
    actions.setPlayerMatches([]);
    actions.setError(null);
    actions.setHasSearched(true);
    actions.setIsLoading(false);
    
    // Reset search job
    actions.resetSearchState();
  }, [actions]);

  return {
    recentPlayers,
    addPlayer,
    findPlayer,
    findPlayerByName,
    loadCachedPlayer,
    clearCache: () => setRecentPlayers([])
  };
};